/**
 * Simple but comprehensive logger utility
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  SILENT = 4,
}

export interface LoggerConfig {
  level: LogLevel;
  prefix?: string;
  enableTimestamp?: boolean;
  enableColors?: boolean;
  enableConsole?: boolean;
  customHandler?: (level: LogLevel, message: string, data?: any) => void;
}

export interface LogEntry {
  timestamp: string;
  level: string;
  prefix?: string;
  message: string;
  data?: any;
}

/**
 * Get log level from URL query parameter
 */
function getLogLevelFromURL(): LogLevel {
  if (typeof window === 'undefined') {
    return LogLevel.SILENT;
  }

  try {
    // Check if URLSearchParams is available
    if (typeof window.URLSearchParams === 'undefined') {
      return LogLevel.SILENT;
    }

    const urlParams = new window.URLSearchParams(window.location.search);
    const logLevelParam = urlParams.get('log_level');

    if (!logLevelParam) {
      return LogLevel.SILENT;
    }

    switch (logLevelParam.toLowerCase()) {
      case 'debug':
        return LogLevel.DEBUG;
      case 'info':
        return LogLevel.INFO;
      case 'warn':
        return LogLevel.WARN;
      case 'error':
        return LogLevel.ERROR;
      case 'silent':
        return LogLevel.SILENT;
      default:
        return LogLevel.SILENT;
    }
  } catch {
    return LogLevel.SILENT;
  }
}

/**
 * Logger class with configurable levels and output options
 */
export class Logger {
  private config: LoggerConfig;
  private levelNames: Record<LogLevel, string> = {
    [LogLevel.DEBUG]: 'DEBUG',
    [LogLevel.INFO]: 'INFO',
    [LogLevel.WARN]: 'WARN',
    [LogLevel.ERROR]: 'ERROR',
    [LogLevel.SILENT]: 'SILENT',
  };

  private levelColors: Record<LogLevel, string> = {
    [LogLevel.DEBUG]: '#6c757d', // gray
    [LogLevel.INFO]: '#007bff', // blue
    [LogLevel.WARN]: '#ffc107', // yellow
    [LogLevel.ERROR]: '#dc3545', // red
    [LogLevel.SILENT]: '#000000', // black
  };

  constructor(config: Partial<LoggerConfig> = {}) {
    const urlLevel = getLogLevelFromURL();
    this.config = {
      level: urlLevel,
      enableTimestamp: true,
      enableColors: true,
      enableConsole: true,
      ...config,
    };
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current logger configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level && level !== LogLevel.SILENT;
  }

  /**
   * Format log message with timestamp and prefix
   */
  private formatMessage(level: LogLevel, message: string): string {
    const parts: string[] = [];

    if (this.config.enableTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    parts.push(`[${this.levelNames[level]}]`);
    parts.push(message);

    return parts.join(' ');
  }

  /**
   * Output log to console with styling
   */
  private outputToConsole(level: LogLevel, message: string, data?: any): void {
    if (!this.config.enableConsole) {
      return;
    }

    const formattedMessage = this.formatMessage(level, message);

    // Choose console method based on level
    const consoleMethod =
      level === LogLevel.ERROR ? 'error' : level === LogLevel.WARN ? 'warn' : 'log';

    // Apply colors if enabled and in browser
    if (this.config.enableColors && typeof window !== 'undefined') {
      const color = this.levelColors[level];
      console[consoleMethod](
        `%c${formattedMessage}`,
        `color: ${color}; font-weight: bold`,
        data || ''
      );
    } else {
      console[consoleMethod](formattedMessage, data || '');
    }
  }

  /**
   * Call custom handler if configured
   */
  private callCustomHandler(level: LogLevel, message: string, data?: any): void {
    if (this.config.customHandler && this.shouldLog(level)) {
      try {
        this.config.customHandler(level, message, data);
      } catch (error) {
        console.error('Error in custom logger handler:', error);
      }
    }
  }

  /**
   * Internal log method
   */
  private log(level: LogLevel, message: string, data?: any): void {
    if (!this.shouldLog(level)) {
      return;
    }

    this.outputToConsole(level, message, data);
    this.callCustomHandler(level, message, data);
  }

  /**
   * Log debug message
   */
  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, data);
  }

  /**
   * Log info message
   */
  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, message, data);
  }

  /**
   * Log warning message
   */
  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, message, data);
  }

  /**
   * Log error message
   */
  error(message: string, data?: any): void {
    this.log(LogLevel.ERROR, message, data);
  }

  /**
   * Create a child logger with additional prefix
   */
  child(prefix: string, configOverrides: Partial<LoggerConfig> = {}): Logger {
    const childPrefix = this.config.prefix ? `${this.config.prefix}:${prefix}` : prefix;
    return new Logger({
      ...this.config,
      ...configOverrides,
      prefix: childPrefix,
    });
  }

  /**
   * Create a logger for a specific module/component
   */
  module(moduleName: string, configOverrides: Partial<LoggerConfig> = {}): Logger {
    return this.child(moduleName, configOverrides);
  }

  /**
   * Log performance timing
   */
  time(label: string): () => void {
    const startTime = performance.now();
    this.debug(`Timer started: ${label}`);

    return () => {
      const duration = performance.now() - startTime;
      this.info(`Timer completed: ${label}`, { duration: `${duration.toFixed(2)}ms` });
    };
  }

  /**
   * Log with context (adds structured data)
   */
  withContext(context: Record<string, any>): Logger {
    return new Logger({
      ...this.config,
      customHandler: (level, message, data) => {
        const enrichedData = { ...context, ...data };
        this.callCustomHandler(level, message, enrichedData);
      },
    });
  }
}

/**
 * Default logger instance
 */
export const logger = new Logger({
  level: getLogLevelFromURL(),
  prefix: 'SirmaAI',
});

/**
 * Convenience function to get a logger for a specific module
 */
export function getLogger(moduleName: string, config: Partial<LoggerConfig> = {}): Logger {
  return logger.module(moduleName, config);
}

// Export default logger
export default logger;
